import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Search, UserCheck, Building2, MapPin, UserPlus, X } from 'lucide-react';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Donor, Office, UpdateDonorData } from '../../lib/sdk/index';
import { formatDate, getDateField } from '../../lib/utils/formatting';
import { toast } from 'react-toastify';
import { useDonors, useOffices, useUpdateDonor, useDeleteDonor } from '../../hooks/useQueries';
import { getErrorMessage } from '../../lib/utils/errorMessage';

const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => window.clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const ManageDonors: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const limit = 50;
  const debouncedSearchTerm = useDebounce(searchTerm, 350);
  
  // Use React Query hooks for cached data with pagination
  const { data: donorsResponse, isLoading: donorsLoading, refetch: refetchDonors } = useDonors({ 
    page, 
    limit, 
    search: debouncedSearchTerm || undefined,
    officeId: officeFilter || undefined
  });
  const { data: officesData = [], isLoading: officesLoading } = useOffices();
  
  const donors = Array.isArray(donorsResponse?.data) ? donorsResponse.data : [];
  const pagination = donorsResponse?.pagination;
  const offices = Array.isArray((officesData as any)?.data) ? (officesData as any).data : [];
  const loading = donorsLoading || officesLoading;
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingDonor, setEditingDonor] = useState<Donor | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteDonorId, setDeleteDonorId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<UpdateDonorData>({});
  const [editErrors, setEditErrors] = useState<{[key: string]: string}>({});
  const [showOfficeDropdown, setShowOfficeDropdown] = useState(false);
  const [officeSearchTerm, setOfficeSearchTerm] = useState('');

  // Mutations
  const updateDonorMutation = useUpdateDonor({
    onSuccess: () => {
      toast.success('Donor updated successfully!');
      setShowEditForm(false);
      setEditingDonor(null);
      refetchDonors();
    },
    onError: (error: any) => {
      console.error('Error updating donor:', error);
      toast.error(getErrorMessage(error, 'Failed to update donor'));
    }
  });

  const deleteDonorMutation = useDeleteDonor({
    onSuccess: () => {
      toast.success('Donor deleted successfully!');
      setShowDeleteConfirm(false);
      setDeleteDonorId(null);
      refetchDonors();
    },
    onError: (error: any) => {
      console.error('Error deleting donor:', error);
      const message = getErrorMessage(error, 'Failed to delete donor');
      toast.error(message);
      setShowDeleteConfirm(false);
      setDeleteDonorId(null);
    }
  });

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showOfficeDropdown) {
        setShowOfficeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOfficeDropdown]);


  const handleEdit = (donor: Donor) => {
    setEditingDonor(donor);
    setEditFormData({
      name: donor.name,
      fatherOrHusbandName: donor.fatherOrHusbandName,
      relation: donor.relation,
      cnic: donor.cnic,
      phone: donor.phone,
      email: donor.email,
      officeId: donor.officeId 
        ? (typeof donor.officeId === 'string' 
            ? donor.officeId 
            : (donor.officeId && typeof donor.officeId === 'object' && '_id' in donor.officeId)
              ? (donor.officeId as any)._id
              : '')
        : ''
    });
    setOfficeSearchTerm(
      donor.officeId
        ? (typeof donor.officeId === 'string' 
            ? offices.find(o => o._id === donor.officeId)?.name || ''
            : (donor.officeId && typeof donor.officeId === 'object' && 'name' in donor.officeId)
              ? (donor.officeId as any).name
              : '')
        : ''
    );
    setShowEditForm(true);
    setEditErrors({});
  };

  const handleUpdate = async () => {
    if (!editingDonor) return;

    // Validate form
    const newErrors: {[key: string]: string} = {};
    if (editFormData.cnic && !/^\d{5}-\d{7}-\d$/.test(editFormData.cnic)) {
      newErrors.cnic = 'CNIC must be in format XXXXX-XXXXXXX-X';
    }
    // Phone is optional, but if provided, validate format
    if (editFormData.phone && !/^\+92-\d{10}$/.test(editFormData.phone)) {
      newErrors.phone = 'Phone must be in format +92-XXXXXXXXXX';
    }
    if (editFormData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editFormData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (Object.keys(newErrors).length > 0) {
      setEditErrors(newErrors);
      return;
    }

    updateDonorMutation.mutate({
      id: editingDonor._id,
      data: editFormData
    });
  };

  const handleDelete = (id: string) => {
    setDeleteDonorId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (!deleteDonorId) return;
    deleteDonorMutation.mutate(deleteDonorId);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteDonorId(null);
  };

  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, officeFilter]);

  const filteredOffices = offices.filter(office =>
    office.name.toLowerCase().includes(officeSearchTerm.toLowerCase()) ||
    office.officeNo.toString().includes(officeSearchTerm)
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Manage Donors</h1>
        <p className="text-gray-600 dark:text-gray-400">View, edit, and manage permanent donors</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <UserCheck className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Donors</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{pagination?.total || donors.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <Building2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Offices with Donors</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {new Set(donors
                  .filter(donor => donor.officeId != null)
                  .map(donor => 
                    typeof donor.officeId === 'string' 
                      ? donor.officeId 
                      : (donor.officeId && typeof donor.officeId === 'object' && '_id' in donor.officeId)
                        ? (donor.officeId as any)._id
                        : null
                  )
                  .filter((id): id is string => id != null)
                ).size}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <MapPin className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Central Office Donors</p>
                               <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                   {donors.filter(donor => {
                     if (!donor.officeId) return false;
                     let officeId: string;
                     if (typeof donor.officeId === 'string') {
                       officeId = donor.officeId;
                     } else if (donor.officeId && typeof donor.officeId === 'object' && '_id' in donor.officeId) {
                       officeId = (donor.officeId as any)._id;
                     } else {
                       return false;
                     }
                     const office = offices.find(o => o._id === officeId);
                     return office?.isCentral;
                   }).length}
                 </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <UserPlus className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Regional Donors</p>
                               <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                   {donors.filter(donor => {
                     if (!donor.officeId) return false;
                     let officeId: string;
                     if (typeof donor.officeId === 'string') {
                       officeId = donor.officeId;
                     } else if (donor.officeId && typeof donor.officeId === 'object' && '_id' in donor.officeId) {
                       officeId = (donor.officeId as any)._id;
                     } else {
                       return false;
                     }
                     const office = offices.find(o => o._id === officeId);
                     return !office?.isCentral;
                   }).length}
                 </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="text"
              placeholder="Search donors by ID, name, CNIC, phone, or office..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={officeFilter}
            onChange={(e) => setOfficeFilter(e.target.value)}
            className="form-select md:col-span-1"
          >
            <option value="">All offices</option>
            {offices.map((office) => (
              <option key={office._id} value={office._id}>
                {office.name} (Office #{office.officeNo})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Donors Table */}
      <div className="card">
        {donors.length === 0 ? (
          <div className="text-center py-12">
            <UserCheck className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No donors found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by adding your first donor.
            </p>
          </div>
        ) : (
          <>
            <TableRoot>
              <Table>
                <THead>
                  <TR>
                    <TH>Donor ID</TH>
                    <TH>Name</TH>
                    <TH>CNIC</TH>
                    <TH>Phone</TH>
                    <TH>Email</TH>
                    <TH>Office</TH>
                    <TH>Created Date</TH>
                    <TH>Actions</TH>
                  </TR>
                </THead>
                <TBody>
                  {donors.map((donor) => (
                    <TR key={donor._id}>
                      <TD className="font-medium">{donor.donorId}</TD>
                      <TD>
                        <div>
                          <div className="font-medium">{donor.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {donor.fatherOrHusbandName} ({donor.relation})
                          </div>
                        </div>
                      </TD>
                      <TD className="font-mono">{donor.cnic}</TD>
                      <TD>{donor.phone}</TD>
                      <TD>{donor.email || '-'}</TD>
                      <TD>
                        {donor.officeId && typeof donor.officeId === 'object' && 'name' in donor.officeId ? (
                          <div>
                            <div className="font-medium">{(donor.officeId as any).name}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              Office #{(donor.officeId as any).officeNo || 'N/A'}
                            </div>
                          </div>
                        ) : (
                          (() => {
                            const officeId = donor.officeId 
                              ? (typeof donor.officeId === 'string' 
                                  ? donor.officeId 
                                  : (donor.officeId && typeof donor.officeId === 'object' && '_id' in donor.officeId)
                                    ? (donor.officeId as any)._id
                                    : null)
                              : null;
                            const office = officeId ? offices.find(o => o._id === officeId) : null;
                            return office ? (
                              <div>
                                <div className="font-medium">{office.name}</div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  Office #{office.officeNo}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-500">-</span>
                            );
                          })()
                        )}
                      </TD>
                      <TD>{formatDate(getDateField(donor))}</TD>
                      <TD>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(donor)}
                            className="flex items-center gap-1"
                          >
                            <Edit className="h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(donor._id)}
                            className="flex items-center gap-1 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableRoot>
            
            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} donors
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={pagination.page === 1 || loading}
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-4 text-sm text-gray-700 dark:text-gray-300">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                    disabled={pagination.page === pagination.totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Form Modal */}
      {showEditForm && editingDonor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Edit Donor: {editingDonor.name}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEditForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Donor Name</label>
                  <Input
                    type="text"
                    value={editFormData.name || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    placeholder="Enter donor's full name"
                  />
                </div>

                <div>
                  <label className="form-label">Father/Husband Name</label>
                  <Input
                    type="text"
                    value={editFormData.fatherOrHusbandName || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, fatherOrHusbandName: e.target.value })}
                    placeholder="Enter father or husband name"
                  />
                </div>

                <div>
                  <label className="form-label">Relation</label>
                  <select
                    value={editFormData.relation || 'Father'}
                    onChange={(e) => setEditFormData({ ...editFormData, relation: e.target.value as 'Father' | 'Husband' })}
                    className="form-select"
                  >
                    <option value="Father">Father</option>
                    <option value="Husband">Husband</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">CNIC</label>
                  <Input
                    type="text"
                    value={editFormData.cnic || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, cnic: e.target.value })}
                    placeholder="XXXXX-XXXXXXX-X"
                    className={editErrors.cnic ? 'border-red-500' : ''}
                  />
                  {editErrors.cnic && <p className="text-red-500 text-xs mt-1">{editErrors.cnic}</p>}
                </div>

                <div>
                  <label className="form-label">Phone Number</label>
                  <Input
                    type="text"
                    value={editFormData.phone || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                    placeholder="Add number without 0, e.g. 3331112223"
                    maxLength={14}
                    className={editErrors.phone ? 'border-red-500' : ''}
                  />
                  {editErrors.phone && <p className="text-red-500 text-xs mt-1">{editErrors.phone}</p>}
                </div>

                <div>
                  <label className="form-label">Email</label>
                  <Input
                    type="email"
                    value={editFormData.email || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    placeholder="Enter email address"
                    className={editErrors.email ? 'border-red-500' : ''}
                  />
                  {editErrors.email && <p className="text-red-500 text-xs mt-1">{editErrors.email}</p>}
                </div>

                <div>
                  <label className="form-label">Office</label>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Search offices..."
                      value={officeSearchTerm}
                      onChange={(e) => {
                        setOfficeSearchTerm(e.target.value);
                        setShowOfficeDropdown(true);
                      }}
                      onFocus={() => setShowOfficeDropdown(true)}
                    />
                    
                    {/* Office dropdown */}
                    {showOfficeDropdown && (
                      <div className="absolute z-10 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1">
                        {filteredOffices.map((office) => (
                          <div
                            key={office._id}
                            className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              
                              setEditFormData({ ...editFormData, officeId: office._id });
                              setOfficeSearchTerm(`${office.name} (Office #${office.officeNo})`);
                              setShowOfficeDropdown(false);
                            }}
                          >
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {office.name} - Office #{office.officeNo}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {office.isCentral ? 'Central Office' : 'Regional Office'}
                            </div>
                          </div>
                        ))}
                        {filteredOffices.length === 0 && (
                          <div className="px-3 py-2 text-gray-500 dark:text-gray-400">
                            {officeSearchTerm ? 'No offices found' : 'Type to search offices...'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <Button onClick={handleUpdate} className="flex-1">
                  Update Donor
                </Button>
                <Button variant="outline" onClick={() => setShowEditForm(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Confirm Deletion</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete this donor? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={cancelDelete} className="flex-1">
                Cancel
              </Button>
              <Button variant="error" onClick={confirmDelete} className="flex-1">
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageDonors;
